import { marketData, marketDataGateway } from '@core/market-data-sdk';
import { makeBenchmarkColor } from '@mais-retorno-front/data-access-test-2/factories';
import { formatDate } from '@mais-retorno-front/helpers/date';

import { IChartSeries } from '@mais-retorno-front/models/chart-serie';
import themeVariables from '@mais-retorno-front/theme/theme-variables';

interface IGetSeriesProps {
  identifier: string;
  window: string;
  color?: string;
  period: { startDate: number; endDate: number };
  accessToken?: string;
  currency?: string;
}

interface IGetBenchmarksProps {
  slug: string;
  window: string;
  color?: string;
  currency?: string;
  period: { startDate: number; endDate: number };
}

export const getSeries = async ({
  color,
  period,
  identifier,
  window,
  currency
}: IGetSeriesProps): Promise<IChartSeries> => {
  const data = await marketDataGateway.general.window.getAccumulatedReturn(
    identifier,
    {
      month: window,
      start_date: formatDate(period.startDate, 'YYYY-MM-DD'),
      end_date: formatDate(period.endDate, 'YYYY-MM-DD'),
      currency
    }
  );

  return {
    id: data.nicename,
    name: data.nicename,
    color,
    type: 'line',
    opacity: 1,
    data: data.quotes.map((quote) => [new Date(quote.d).getTime(), quote.c]),
  };
};

export const getBenchmarksSeries = async ({
  period,
  slug,
  window,
  currency
}: IGetBenchmarksProps): Promise<IChartSeries> => {
  const data = await marketDataGateway.general.window.getAccumulatedReturn(
    `${slug}:idx`,
    {
      month: window,
      start_date: formatDate(period.startDate, 'YYYY-MM-DD'),
      end_date: formatDate(period.endDate, 'YYYY-MM-DD'),
      currency
    }
  );

  return {
    id: slug,
    name: data.nicename,
    type: 'line',
    opacity: 0.5,
    color: makeBenchmarkColor().getColor(slug),
    data: data.quotes.map((quote) => [new Date(quote.d).getTime(), quote.c]),
  };
};

export const getFundBoxPlotSeries = async (
  slug: string,
  color: string
): Promise<IChartSeries> => {
  const data = await marketData.general.window.getBoxplot(`${slug}:fi`);

  return {
    id: data.slug,
    name: data.nicename,
    color,
    data: Object.values(data.timeframe).map((item) => [
      item.min,
      item.max,
    ]) as any,
  };
};

export const getBenchmarkBoxPlotSeries = async (
  index: string
): Promise<IChartSeries> => {
  const data = await marketData.general.window.getBoxplot(`${index}:idx`);

  return {
    id: index,
    name: data.nicename,
    color: makeBenchmarkColor().getColor(index),
    data: Object.values(data.timeframe).map((item) => [
      item.min,
      item.max,
    ]) as any,
  };
};

export const getSuccessRateSeries = async ({
  identifier,
  benchmark,
  currency
}: {
  identifier: string;
  benchmark?: string;
  currency?: string;
}): Promise<IChartSeries> => {
  const serieName = benchmark
    ? `Ativo vs. ${benchmark.toLocaleUpperCase()}`
    : 'Rentabilidade > 0%';

  const data = await marketDataGateway.general.window.getSuccessRate(
    identifier,
    benchmark,
    currency
  );

  const filteredData = Object.entries(data.timeframe).filter(([key, value]) => {
    const months = parseInt(key.match(/hist_(\d+)_months/)[1]);
    return months <= 60;
  });

  return {
    id: benchmark ? benchmark : identifier,
    name: serieName,
    type: 'column',
    color: benchmark
      ? makeBenchmarkColor().getColor(benchmark)
      : themeVariables.colors.secondary,
    opacity: 1,
    data: filteredData.map(([_, value]) => value.success_percent),
  };
};
